import { createSelector } from '@reduxjs/toolkit';

import { UploaderShape, UploaderMode, JobStatus, jobIsSettled } from './model';
import { RootSelector, getStore } from '../../models/rootSelector';
import { getCurrentNode, getBreadcrumbs } from '../breadcrumbs/selectors';

import { UserAccessRight } from '../../../general/model';
import zipObject from 'lodash/zipObject';
import { CHNodeIsCreativeFolder } from '../../../general/node';

export const getUploader: RootSelector<UploaderShape> = createSelector(getStore, (x) => x.uploader);
export const getJobs = createSelector(getUploader, (uploader) => uploader.jobs);

export const getIsPending = createSelector(
  getUploader,
  (uploader) => uploader.pendingJobs.length !== 0,
);

export const getFirstEnqueuedJob = createSelector(
  getJobs,
  (jobs) => jobs.find((job) => job.status === JobStatus.ENQUEUED) || null,
);
export const getGroupNames = createSelector(getUploader, (up) => up.groupNames);

export const getEnabled = createSelector([getCurrentNode], (currentNode) => {
  if (!currentNode) {
    return false;
  }
  if (!currentNode.perms.includes(UserAccessRight.write)) {
    return false;
  }
  return true;
});

export const getUploaderMode = createSelector([getBreadcrumbs], (breadcrumbs) => {
  return breadcrumbs.find(CHNodeIsCreativeFolder)
    ? UploaderMode.CREATIVE_FILE
    : UploaderMode.REGULAR_NODE;
});

export const getGroups = createSelector(getJobs, (jobs) => [
  ...new Set(jobs.map((job) => job.groupID)),
]);

export const getJobsGrouped = createSelector([getJobs, getGroups], (jobs, groups) => {
  return groups.map((groupID) => jobs.filter((job) => job.groupID === groupID));
});

export const getJobsKeyed = createSelector(getJobs, getGroups, (jobs, groups) => {
  const values = groups.map((groupID) => jobs.filter((job) => job.groupID === groupID));
  return zipObject(groups, values);
});

export const getAreThereRunningJobs = createSelector(getJobs, (jobs) => {
  return !jobs.every(jobIsSettled);
});
